import { useEffect, useMemo, useState } from 'react';
import { Layout } from '@frontend/components/Layout';
import { useAuth } from '@frontend/components/AuthProvider';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Alert,
  MenuItem,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface TokenListItem {
  publicId: string;
  revoked: boolean;
  expiresAt: number | null;
  scopes: string[];
}

export default function Tokens(): JSX.Element {
  const { activeOrganization, activeRole } = useAuth();
  const isAdmin = activeRole === 'admin';

  const [tokens, setTokens] = useState<TokenListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scopesAvailable, setScopesAvailable] = useState<string[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expiryPreset, setExpiryPreset] = useState<'never' | '1d' | '7d' | '30d'>('30d');
  const [creating, setCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const expiresAt = useMemo(() => {
    if (expiryPreset === 'never') return null;
    const now = Date.now();
    const days = expiryPreset === '1d' ? 1 : expiryPreset === '7d' ? 7 : 30;
    return now + days * 24 * 60 * 60 * 1000;
  }, [expiryPreset]);

  const load = async () => {
    if (!activeOrganization) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/tokens');
      if (!res.ok) throw new Error(await res.text());
      const data = (await res.json()) as TokenListItem[];
      setTokens(data);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Failed to load tokens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization?.id]);

  // Load available scopes from backend
  useEffect(() => {
    if (!activeOrganization) return;
    (async () => {
      try {
        const res = await fetch('/api/v1/tokens/scopes');
        if (!res.ok) throw new Error(await res.text());
        const data = (await res.json()) as string[];
        setScopesAvailable(data);
      } catch (e) {
        console.error('Failed to load scopes', e);
      }
    })();
  }, [activeOrganization?.id]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    setCreatedToken(null);
    try {
      const scopes = selectedScopes;
      const res = await fetch('/api/v1/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scopes, expiresAt }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = (await res.json()) as { token: string; publicId: string };
      setCreatedToken(data.token);
      await load();
      setSelectedScopes([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/organizations/reset-token', { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      await load(); // Reload tokens to reflect the change
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset token');
    } finally {
      setResetting(false);
      setResetConfirmOpen(false);
    }
  };

  const handleRevoke = async (publicId: string) => {
    setError(null);
    try {
      const res = await fetch(`/api/v1/tokens/${encodeURIComponent(publicId)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke token');
    }
  };

  const copy = (text: string) => navigator.clipboard.writeText(text);

  // Sort tokens by priority:
  // 1. Active/expired tokens that are urgent (expiring/expired within a week)
  // 2. Other active tokens
  // 3. Revoked tokens
  // 4. Other expired tokens
  const sortedTokens = useMemo(() => {
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000;

    return [...tokens].sort((a, b) => {
      const aExpired = a.expiresAt && a.expiresAt < now;
      const bExpired = b.expiresAt && b.expiresAt < now;
      const aExpiringWithinWeek = a.expiresAt && a.expiresAt >= now && a.expiresAt <= now + oneWeek;
      const bExpiringWithinWeek = b.expiresAt && b.expiresAt >= now && b.expiresAt <= now + oneWeek;
      const aExpiredWithinWeek = a.expiresAt && aExpired && a.expiresAt >= now - oneWeek;
      const bExpiredWithinWeek = b.expiresAt && bExpired && b.expiresAt >= now - oneWeek;

      // Priority 1: Urgent tokens (expiring/expired within a week) BUT only if not revoked
      const aUrgent = !a.revoked && (aExpiredWithinWeek || aExpiringWithinWeek);
      const bUrgent = !b.revoked && (bExpiredWithinWeek || bExpiringWithinWeek);
      if (aUrgent && !bUrgent) return -1;
      if (!aUrgent && bUrgent) return 1;

      // Priority 2: Active tokens (not revoked, not expired)
      const aActive = !a.revoked && !aExpired;
      const bActive = !b.revoked && !bExpired;
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;

      // Priority 3: Revoked tokens
      if (a.revoked && !b.revoked) return -1;
      if (!a.revoked && b.revoked) return 1;

      // Priority 4: Other expired tokens (already expired, not within a week)
      // These are naturally at the bottom

      return 0;
    });
  }, [tokens]);

  return (
    <Layout title="API Tokens">
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 120px)' }}>
        <Box sx={{ flexGrow: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isAdmin && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Create Token
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <Select
                  multiple
                  displayEmpty
                  size="small"
                  value={selectedScopes}
                  onChange={(e) =>
                    setSelectedScopes(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))
                  }
                  renderValue={(selected) =>
                    (selected as string[]).length === 0 ? (
                      <span style={{ color: 'rgba(0,0,0,0.6)' }}>Select scopes</span>
                    ) : (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                        {(selected as string[]).map((s) => (
                          <Chip key={s} size="small" label={s} sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </Stack>
                    )
                  }
                  fullWidth
                >
                  {scopesAvailable.map((scope) => (
                    <MenuItem key={scope} value={scope}>
                      {scope}
                    </MenuItem>
                  ))}
                </Select>
                <Select size="small" value={expiryPreset} onChange={(e) => setExpiryPreset(e.target.value as any)}>
                  <MenuItem value="1d">1 day</MenuItem>
                  <MenuItem value="7d">7 days</MenuItem>
                  <MenuItem value="30d">30 days</MenuItem>
                  <MenuItem value="never">Never</MenuItem>
                </Select>
                <Button onClick={handleCreate} disabled={creating} variant="contained">
                  Create
                </Button>
              </Stack>
              {createdToken && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {createdToken}
                    </Typography>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copy(createdToken)}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" display="block">
                    This is the only time you will see the token value. Copy and store it securely.
                  </Typography>
                </Alert>
              )}
            </Paper>
          )}

          <Paper>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1">Existing Tokens</Typography>
            </Box>
            <Divider />
            <Box>
              {loading ? (
                <Box sx={{ p: 2 }}>Loading...</Box>
              ) : sortedTokens.length === 0 ? (
                <Box sx={{ p: 2, color: 'text.secondary' }}>No tokens yet.</Box>
              ) : (
                sortedTokens.map((t) => {
                  const now = Date.now();
                  const oneWeek = 7 * 24 * 60 * 60 * 1000;
                  const isExpired = t.expiresAt && t.expiresAt < now;
                  const isExpiringWithinWeek = t.expiresAt && t.expiresAt >= now && t.expiresAt <= now + oneWeek;
                  const isExpiredWithinWeek = t.expiresAt && isExpired && t.expiresAt >= now - oneWeek;
                  const isUrgent = isExpiringWithinWeek || isExpiredWithinWeek;

                  const status = t.revoked ? 'revoked' : isExpired ? 'expired' : 'active';
                  const statusColor = t.revoked ? 'default' : isExpired ? 'warning' : 'success';
                  const statusVariant = t.revoked || isExpired ? 'outlined' : 'filled';

                  return (
                    <Box
                      key={t.publicId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 0 },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {t.publicId}
                        </Typography>
                        {t.scopes.length > 0 ? (
                          <Stack direction="row" spacing={0.5}>
                            {t.scopes.map((s) => (
                              <Chip size="small" key={s} label={s} />
                            ))}
                          </Stack>
                        ) : (
                          <Chip size="small" label="no scopes" variant="outlined" />
                        )}
                        <Chip size="small" color={statusColor} label={status} variant={statusVariant} />
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t.expiresAt ? `expires ${new Date(t.expiresAt).toLocaleString()}` : 'never expires'}
                          </Typography>
                          {isUrgent && <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 18 }} />}
                        </Stack>
                      </Stack>
                      {isAdmin && !t.revoked && !isExpired ? (
                        <Button color="error" size="small" onClick={() => handleRevoke(t.publicId)}>
                          Revoke
                        </Button>
                      ) : (
                        <span />
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          </Paper>
        </Box>

        {isAdmin && (
          <Paper sx={{ mt: 'auto', border: 1, borderColor: 'error.main' }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" color="error" gutterBottom>
                Danger Zone
              </Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography>Reset the primary organization token. There's usually no reason to do this.</Typography>
                <Button variant="contained" color="error" onClick={() => setResetConfirmOpen(true)} disabled={resetting}>
                  Reset Organization Token
                </Button>
              </Stack>
            </Box>
          </Paper>
        )}

        <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)}>
          <DialogTitle>Reset API Token?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to reset the primary organization token? This action cannot be undone, and the previous token will be
              immediately invalidated.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleReset} color="error" disabled={resetting}>
              Reset
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
